import type { QuoteBudgetLine, QuoteBudgetPeopleCounts } from "../types";
import type { RailRoleFormulaMap, RailTravelerRole } from "../types/localDraft";
import { calculateLineAmount } from "./quoteBudgetCalculationService";
import { buildManualDraftLines, parseManualLineFormula } from "./quoteBudgetManualEntryService";

export type { RailRoleFormulaMap, RailTravelerRole };
/** 火车修改弹窗可选角色，顺序固定为学生、老师、导游、司机。 */
export const RAIL_ROLES = ["学生", "老师", "导游", "司机"] as const satisfies readonly RailTravelerRole[];

/** 新旧火车票共用角色分价入口，手续费仍使用原公式。 */
export function isRailTicketLine(line: QuoteBudgetLine | null | undefined): boolean {
  return Boolean(line && !line.entrySnapshot?.railFeeKind && line.resourceType === "大交通" && line.resourceEntryType !== "手续费"
    && (line.resourceEntryType === "火车" || /火车|铁路/u.test(line.itemSpecSnapshot ?? "")));
}

/** 子费用保留路线与计价口径，不继承车票资源、转换凭据或来源价格。 */
function railFeeBase(ticket: QuoteBudgetLine, id: string): QuoteBudgetLine {
  const snapshot = railFormulaSubject(ticket, ticket.travelerRole as RailTravelerRole).entrySnapshot!;
  return {
    budgetLineId: id, lineNo: ticket.lineNo, resourceType: "大交通", resourceEntryType: "火车",
    itemSpecSnapshot: "铁路附加费", travelerRole: ticket.travelerRole, unit: "人",
    quantity: ticket.quantity, budgetUnitPrice: "5.00", budgetAmount: "0.00",
    priceSourceType: "manual", peopleMode: ticket.peopleMode,
    manualPeopleCount: ticket.manualPeopleCount, isLinkedToPeopleCount: ticket.isLinkedToPeopleCount,
    isCapitaShared: ticket.isCapitaShared, isAgentCollection: ticket.isAgentCollection,
    entrySnapshot: { schemaVersion: 1, inputMode: "manual_v2", groupId: snapshot.groupId,
      groupLabel: snapshot.groupLabel, quantityMode: snapshot.quantityMode, billingCount: 1 },
  };
}

/** 每个已分价票价角色生成固定每人五元的手续费，关联键不依赖数据库复制后的行 ID。 */
export function buildRailServiceFees(tickets: QuoteBudgetLine[], createId: () => string = createRailLineId): QuoteBudgetLine[] {
  return tickets.filter(ticket => ticket.travelerRole).map(ticket => {
    const fee = railFeeBase(ticket, createId());
    fee.itemNameSnapshot = "手续费";
    fee.entrySnapshot = { ...fee.entrySnapshot!, railFeeKind: "service",
      railTicketKey: ticket.entrySnapshot?.railTicketKey ?? ticket.budgetLineId };
    fee.budgetAmount = calculateLineAmount(fee);
    return fee;
  });
}

/** 自定义费用名称自由输入；复用金额解析器校验人数与单价，省略人数时联动角色总人数。 */
export function buildRailCustomFee(route: QuoteBudgetLine, input: { name: string; role: RailTravelerRole; formula: string }, people: QuoteBudgetPeopleCounts, createId: () => string = createRailLineId): QuoteBudgetLine | null {
  const name = input.name.trim();
  if (!name || name.length > 300 || !RAIL_ROLES.includes(input.role)) return null;
  const subject = railFeeBase(route, createId());
  subject.itemNameSnapshot = name;
  subject.travelerRole = input.role;
  subject.entrySnapshot = { ...subject.entrySnapshot!, quantityMode: route.entrySnapshot?.railFeeKind === "custom" && route.travelerRole === input.role ? route.entrySnapshot.quantityMode : "linked_people", railFeeKind: "custom" };
  const parsed = buildManualDraftLines(parseManualLineFormula(input.formula, subject, { category: "air", people, areas: [] }))[0];
  if (!parsed || parsed.priceSourceType === "unpriced") return null;
  return { ...subject, quantity: parsed.quantity, budgetUnitPrice: parsed.budgetUnitPrice,
    budgetAmount: parsed.budgetAmount, peopleMode: parsed.peopleMode,
    manualPeopleCount: parsed.manualPeopleCount, isLinkedToPeopleCount: parsed.isLinkedToPeopleCount,
    entrySnapshot: { ...parsed.entrySnapshot!, railFeeKind: "custom" } };
}

/** 仅在编辑时适配旧票价：人次视为票数，确认前不修改原明细。 */
export function railFormulaSubject(line: QuoteBudgetLine, role: RailTravelerRole): QuoteBudgetLine {
  const snapshot = line.entrySnapshot;
  const label = (snapshot?.groupLabel || line.spuNameSnapshot || line.itemNameSnapshot || "").trim();
  const linked = role !== line.travelerRole || (snapshot
    ? snapshot.quantityMode === "linked_people" : line.isLinkedToPeopleCount);
  return {
    ...line, travelerRole: role, unit: "人",
    manualPeopleCount: linked ? null : String(line.manualPeopleCount ?? line.quantity),
    entrySnapshot: {
      ...snapshot, schemaVersion: 1, inputMode: "manual_v2",
      groupId: snapshot?.groupId || `route:rail:${label}`, groupLabel: label,
      quantityMode: linked ? "linked_people" : "fixed_people", billingCount: 1,
    },
  };
}

/** 铁路查询多选确认时带回的票价，只保留写回核价需要的字段。 */
export type RailFareAssignment = {
  trainCode: string;
  originName: string;
  destinationName: string;
  departTime: string;
  arriveTime: string;
  seatName: string;
  berthName: string | null;
  price: number;
  date: string;
};

/** 生成核价行 ID；测试可替换，避免依赖浏览器随机源。 */
export function createRailLineId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  return `rail-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** 把票价规整为两位小数；非法价格不生成明细。 */
function railUnitPrice(price: number): string | null {
  if (!Number.isFinite(price) || price < 0) return null;
  const cents = Math.round(price * 100);
  return `${Math.trunc(cents / 100)}.${String(cents % 100).padStart(2, "0")}`;
}

/** 多选票价各生成一行；未选角色前人数和金额留空，同路线共享小计。 */
export function buildRailQuoteLines(fares: RailFareAssignment[], createId: () => string = createRailLineId): QuoteBudgetLine[] {
  return fares.flatMap((fare, index) => {
    const unitPrice = railUnitPrice(fare.price);
    const route = `${fare.originName}-${fare.destinationName}`;
    const seat = `${fare.seatName}${fare.berthName ? ` ${fare.berthName}` : ""}`;
    if (!unitPrice || !fare.trainCode || !fare.originName || !fare.destinationName || !fare.seatName) return [];
    return [{
      budgetLineId: createId(),
      lineNo: index + 1,
      resourceType: "大交通",
      resourceEntryType: "火车",
      itemNameSnapshot: `${fare.trainCode} ${seat}`,
      itemSpecSnapshot: "铁路票价",
      travelerRole: null,
      unit: "人",
      quantity: "0",
      budgetUnitPrice: unitPrice,
      budgetAmount: "0.00",
      priceSourceType: "manual",
      priceBasisDesc: `${fare.date} ${fare.trainCode} ${route} ${fare.departTime}-${fare.arriveTime} ${seat}`,
      peopleMode: "linked",
      manualPeopleCount: null,
      isLinkedToPeopleCount: true,
      isCapitaShared: true,
      isAgentCollection: true,
      entrySnapshot: {
        schemaVersion: 1,
        inputMode: "manual_v2",
        groupId: `rail:${route}`,
        groupLabel: route,
        quantityMode: "linked_people",
        billingCount: 1,
      },
    }];
  });
}

/** 火车角色对应的统一人数；未知角色不带人数。 */
export function railPeopleCount(role: string, people: QuoteBudgetPeopleCounts): number | null {
  const counts: Record<string, number> = {
    学生: people.studentCount,
    老师: people.teacherCount,
    导游: people.guideCount ?? 0,
    司机: people.driverCount ?? 0,
  };
  return RAIL_ROLES.includes(role as typeof RAIL_ROLES[number]) ? counts[role] ?? 0 : null;
}

/** 去掉金额尾零，供公式输入展示。 */
function railPriceText(value: string | number): string {
  return String(value).trim().replace(/\.00$/u, "").replace(/(\.\d)0$/u, "$1");
}

/** 打开修改弹窗时的公式：未选角色只显示单价，已选角色保留当前人数。 */
export function railModifyFormula(line: QuoteBudgetLine): string {
  const price = railPriceText(line.budgetUnitPrice);
  if (!line.travelerRole) return `${price}元`;
  const people = line.entrySnapshot?.quantityMode === "fixed_people" && line.manualPeopleCount != null
    ? railPriceText(line.manualPeopleCount)
    : railPriceText(line.quantity);
  return `${people}人 x ${price}元`;
}

/** 保留公式里已填写的单价；选角色时只替换人数。 */
export function railFormulaPrice(text: string, fallback: string | number): string {
  const last = text.replace(/\s/gu, "").split(/[xX×*]/u).at(-1)?.replace(/元$/u, "") ?? "";
  return /^\d+(?:\.\d+)?$/u.test(last) ? railPriceText(last) : railPriceText(fallback);
}

/** 勾选角色时的默认公式：统一人数 × 票价。 */
export function railDefaultRoleFormula(role: string, people: QuoteBudgetPeopleCounts, price: string | number): string {
  const count = railPeopleCount(role, people);
  if (count === null) return `${railPriceText(price)}元`;
  return `${count}人 x ${railPriceText(price)}元`;
}

/** 当前已勾选角色，顺序与 RAIL_ROLES 一致。 */
export function selectedRailRoles(railRoles: RailRoleFormulaMap | undefined): RailTravelerRole[] {
  return RAIL_ROLES.filter((role) => Object.prototype.hasOwnProperty.call(railRoles ?? {}, role));
}

/** 展开已勾选角色及其公式，供确认写入与预览汇总。 */
export function railRoleOrder(railRoles: RailRoleFormulaMap | undefined): Array<[RailTravelerRole, string]> {
  return selectedRailRoles(railRoles).map((role) => [role, railRoles?.[role] ?? ""]);
}

/** 打开铁路票价修改时，已有角色预填其公式；未选角色保持空映射。 */
export function initialRailRoleFormulas(line: QuoteBudgetLine): RailRoleFormulaMap {
  if (!line.travelerRole || !RAIL_ROLES.includes(line.travelerRole as RailTravelerRole)) return {};
  return { [line.travelerRole as RailTravelerRole]: railModifyFormula(line) };
}

/** 多角色共用默认票价：优先已填公式单价，否则用明细单价。 */
export function railSharedDefaultPrice(line: QuoteBudgetLine, railRoles: RailRoleFormulaMap | undefined): string {
  for (const [, text] of railRoleOrder(railRoles)) {
    const price = railFormulaPrice(text, "");
    if (price) return price;
  }
  return railPriceText(line.budgetUnitPrice);
}

/** 铁路修改浮层是否仍与当前行一致；仅打开未改勾选/公式时不算未确认。 */
export function railRolesUnchanged(line: QuoteBudgetLine, railRoles: RailRoleFormulaMap | undefined): boolean {
  return JSON.stringify(railRoleOrder(railRoles)) === JSON.stringify(railRoleOrder(initialRailRoleFormulas(line)));
}
